<template>
  <div class="sale-sort">
    <v-menu
      ref="menu"
      v-model="open"
      offset-y
      :left="menuLeft"
      nudge-bottom="4"
      min-width="0"
      max-height="80vh"
      :close-on-content-click="false"
      content-class="app-menu"
      role="none"
    >
      <template v-slot:activator="{ on, attrs }">
        <app-button
          kind="field"
          icon="arrowDownUp"
          chevron
          :class="{ 'app-btn--fixed': !!buttonStyle }"
          :style="buttonStyle"
          :title="'Sortierung: ' + state.text"
          :aria-label="'Sortierung: ' + state.name"
          v-bind="attrs"
          v-on="on"
          @click="onActivatorClick"
        >{{ state.text }}</app-button>
      </template>
      <div
        class="app-menu__list sale-sort__list"
        :class="{ 'sale-sort__list--touch': touch }"
        role="menu"
        aria-label="Sortierung"
        @keydown.tab="onTab"
      >
        <div role="group" aria-label="Sortieren nach">
          <div class="app-menu__header" role="presentation" aria-hidden="true">Sortieren nach</div>
          <button
            v-for="field in fields"
            :id="itemId(field.key)"
            :key="field.key"
            type="button"
            class="v-list-item app-menu__item sale-sort__field"
            role="menuitemradio"
            :aria-checked="state.key === field.key ? 'true' : 'false'"
            :aria-label="field.label + ', ' + fieldDirection(field)"
            @click="selectField(field)"
            @keydown.enter.stop.prevent="selectField(field)"
          >
            <span class="sale-sort__name">{{ field.label }}</span>
            <span class="sale-sort__dir">{{ fieldDirection(field) }}</span>
            <app-icon
              name="check"
              :size="14"
              class="app-menu__check"
              :class="{ 'sale-sort__check--leer': state.key !== field.key }"
            />
          </button>
        </div>
        <template v-if="state.directions.length">
          <div class="app-menu__divider" role="separator"></div>
          <div role="group" aria-label="Richtung">
            <div class="app-menu__header" role="presentation" aria-hidden="true">Richtung</div>
            <button
              v-for="direction in state.directions"
              :id="itemId('richtung-' + direction.dir)"
              :key="direction.dir"
              type="button"
              class="v-list-item app-menu__item"
              role="menuitemradio"
              :aria-checked="state.dir === direction.dir ? 'true' : 'false'"
              @click="selectDirection(direction.dir)"
              @keydown.enter.stop.prevent="selectDirection(direction.dir)"
            >
              <span class="app-menu__text">{{ direction.text }}</span>
              <app-icon v-if="state.dir === direction.dir" name="check" :size="14" class="app-menu__check" />
            </button>
          </div>
        </template>
      </div>
    </v-menu>
  </div>
</template>

<script>
// Feld „Sortierung“ der Verkäufe (Kundenwunsch 12.10.2026: „sortierung nochmal überarbeiten mit besser gesalteter
// labels evtl dick oder was auch immer und nur was auch sinn macht“; Felder und Texte in saleFilters.js). Feldknopf
// mit Pfeilen und kurzem Wert („Neueste zuerst“, „Preis · höchster zuerst“), zugänglicher Name „Sortierung: Preis,
// höchster zuerst“, Tooltip „Sortierung: Preis · höchster zuerst“. Menü in zwei Gruppen:
// - „Sortieren nach“: die sieben Felder, Feldname in 600 (--ink), rechts daneben die Richtung in --ink-3 (beim
//   gewählten Feld die aktuelle, sonst die Standardrichtung, die ein Klick setzt), Haken am gewählten Feld;
// - „Richtung“: zwei Einträge mit den Texten des gewählten Felds („Neueste zuerst“/„Älteste zuerst“, „Höchster
//   zuerst“/„Niedrigster zuerst“, „A–Z“/„Z–A“). Bei einem Kopfklick ohne eigenes Feld (Zahlart, Pers.) ist kein Feld
//   gewählt, die Richtung gilt für die Spalte des Kopfklicks.
// Jede Wahl schließt das Menü. Ereignis input mit der neuen Sortierung ([[spalte, richtung], …]).
// Tastatur und Rollen wie AppSelect (seit der Prüfung vom 12.10.2026 nach dem Muster Menüknopf): Menü role="menu"
// „Sortierung“, Gruppen mit Namen (die sichtbaren Überschriften sind für Screenreader verborgen), Einträge
// menuitemradio mit aria-checked und der Klasse v-list-item. Pfeiltasten, Pos1 und Ende (VMenu) bewegen den Fokus auf
// den Eintrag; mit Enter oder Leertaste geöffnet steht er auf dem gewählten Feld. Enter und Leertaste wählen den
// Eintrag mit Fokus, Esc schließt und gibt den Fokus an den Knopf, Tab schließt und geht vom Knopf aus weiter; nach
// einer Wahl, auch mit der Maus, liegt der Fokus wieder am Knopf (Fokusführung mit followMenuFocus und focusMenuItem
// aus AppSelect).
// minWidth (px): Mindestbreite des Knopfs, damit die Leiste beim Wechsel der Werte nicht springt (Text links,
// Chevron rechts); alignRight: Menü rechtsbündig zum Knopf, passt es dort nicht ins Fenster, linksbündig (Handy
// umgekehrt).
import { raiseMenu, followMenuFocus, focusMenuItem } from '@/components/ui/AppSelect.vue';
import { alignRightFor } from './panelMenu';
import {
  SORT_FIELDS,
  describeSort,
  sortSpecFor,
  sortDirectionText,
  sameSortSpec,
  normalizeSortSpec
} from './saleFilters';

export default {
  name: 'SaleSortMenu',
  props: {
    // Sortierung [[spalte, 'asc' | 'desc'], …]
    value: { type: Array, required: true },
    minWidth: { type: Number, default: null },
    alignRight: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      fields: SORT_FIELDS,
      // Seite am Knopf beim Öffnen (null: wie alignRight)
      rightAligned: null
    };
  },
  computed: {
    state() {
      return describeSort(this.value);
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    buttonStyle() {
      return this.minWidth ? { minWidth: this.minWidth + 'px' } : null;
    },
    menuLeft() {
      return this.rightAligned === null ? this.alignRight : this.rightAligned;
    }
  },
  watch: {
    open(isOpen) {
      if (!isOpen) {
        return;
      }
      // Seite am Knopf: Breite der Liste samt Kante des Menüs
      const button = this.$el.querySelector('.app-btn');
      this.rightAligned = alignRightFor(button, (this.touch ? 304 : 272) + 2, this.alignRight);
      // mit Enter oder Leertaste geöffnet: Fokus auf das gewählte Feld
      if (this.keyboardOpen) {
        focusMenuItem(this.$refs.menu, '[aria-checked="true"]');
      }
      this.keyboardOpen = false;
    }
  },
  created() {
    // nicht reaktiv: zuletzt mit der Tastatur geöffnet (Klick mit detail 0)
    this.keyboardOpen = false;
  },
  mounted() {
    raiseMenu(this.$refs.menu);
    followMenuFocus(this, this.$refs.menu, () => this.open);
  },
  methods: {
    itemId(key) {
      return 'sale-sort-' + this._uid + '-' + key;
    },
    onActivatorClick(e) {
      this.keyboardOpen = !!e && e.detail === 0;
    },
    focusButton() {
      const button = this.$el.querySelector('.app-btn');
      if (button) {
        button.focus();
      }
    },
    // Tab im Menü: schließen und vom Knopf aus weiter (der Browser setzt den Fokus danach auf das nächste Element)
    onTab() {
      this.open = false;
      this.focusButton();
    },
    fieldDirection(field) {
      return sortDirectionText(field, this.state.key === field.key ? this.state.dir : field.dir);
    },
    // anderes Feld: seine Standardrichtung; das gewählte Feld behält seine Richtung
    selectField(field) {
      this.emitSort(sortSpecFor(field.key, this.state.key === field.key ? this.state.dir : field.dir));
    },
    selectDirection(dir) {
      if (this.state.key) {
        this.emitSort(sortSpecFor(this.state.key, dir));
      } else if (this.state.column) {
        this.emitSort([[this.state.column, dir]]);
      }
    },
    emitSort(spec) {
      this.open = false;
      // der gewählte Eintrag verschwindet mit dem Menü: Fokus zurück an den Knopf
      this.focusButton();
      if (!sameSortSpec(normalizeSortSpec(spec), normalizeSortSpec(this.value))) {
        this.$emit('input', spec);
      }
    }
  }
};
</script>

<style scoped>
.sale-sort {
  display: inline-flex;
  flex: none;
  max-width: 100%;
  min-width: 0;
  vertical-align: middle;
}
.sale-sort__list {
  width: 272px;
}
.sale-sort__list--touch {
  width: 304px;
}
/* Feldname fett (Kundenwunsch „evtl dick“), Richtung gedämpft rechts daneben */
.sale-sort__name {
  flex: none;
  font-weight: 600;
  color: var(--ink);
}
.sale-sort__dir {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  font-weight: 400;
  color: var(--ink-3);
}
/* Haken nur am gewählten Feld; bei den übrigen hält er unsichtbar den Platz, die Richtungen stehen bündig */
.sale-sort__check--leer {
  visibility: hidden;
}
</style>
